/* eslint-disable @stylistic/js/max-len */
import { EAxiosResponseCode, getHttpErrorMessage, Http } from '@core/api';
import { changeTimeToFormat, getHostbaseUrl, isNull } from '@core/tools';
import { IFinanceLianAccOpening, IFinanceLianAccount, IFinanceLianAccountListParam, IFinanceLianApplySubmit, IFinanceLianApplyVerify, IFinanceLianBill, IFinanceLianBillListParam, IFinanceLianChangePhone, IFinanceLianDetail, IFinanceLianLicenseParam, IFinanceLianUpdatePayPass, IFinanceLianUpdatePayPassBySms, IFinanceLianWithdraw, IFinanceLianWithdrawVerify } from '@core/main/types';
import { IBeneficiarySaveParam } from '@core/main/src/components';
import { ILegalPersonSaveParam } from '@core/main/src/components/open-account/leagal-person/type';
import { getFinanceLianAccOpeningParam } from './finance-lianlian-utils';

const hostUrl = getHostbaseUrl();

/**
 * 获取资金账户 - 连连账户 - 详细信息
 */
export async function queryFinanceLianDetail(lianId: number) {
  let data = {} as IFinanceLianDetail;

  try {
    const { code, context, message } = await Http.get<any>(
      `${hostUrl}/pay/unified-lian-account/query/detail`,
      { lianId },
    );

    if (code === EAxiosResponseCode.Succeed) {
      data = context || {} as IFinanceLianDetail;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return data;
}

/**
 * 资金账户 - 申请linalian账户 - 上传营业执照信息保存
 */
export async function submitFinanceLianLicense(param: IFinanceLianLicenseParam) {
  let d = {} as IFinanceLianDetail;

  param = JSON.parse(JSON.stringify(param)) as IFinanceLianLicenseParam;

  // @ts-ignore
  delete param.idType;
  delete param.businessLicensePicType;

  if (param.businessLicenseEndDate) {
    param.businessLicenseEndDate = changeTimeToFormat(param.businessLicenseEndDate);
    param.businessLicenseStartDate = changeTimeToFormat(param.businessLicenseStartDate);
  }

  if (param.idNoExpiryStartDate) {
    param.idNoExpiryStartDate = changeTimeToFormat(param.idNoExpiryStartDate);
    param.idNoExpiryEndDate = changeTimeToFormat(param.idNoExpiryEndDate);
  }

  try {
    const { code, context, message } = await Http.post<IFinanceLianDetail>(
      `${hostUrl}/pay/unified-lian-account/save-business-license`,
      param,
    );

    if (code === EAxiosResponseCode.Succeed) {
      d = context || {} as IFinanceLianDetail;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    ...d,
    lianId: Number(d.lianId),
  };
}

/**
 * 资金账户 - 申请linalian账户 - 法人信息保存
 */
export async function submitFinanceLegal(param: ILegalPersonSaveParam) {
  param = JSON.parse(JSON.stringify(param)) as ILegalPersonSaveParam;

  param.idNo = param.legalIdNo;

  delete param.idNoBackType;
  delete param.idNoFrontType;
  delete param.legalIdNo;

  param.idNoExpiryStartDate = changeTimeToFormat(param.idNoExpiryStartDate);
  param.idNoExpiryEndDate = changeTimeToFormat(param.idNoExpiryEndDate);

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/unified-lian-account/save-legal-person-info`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 资金账户 - 申请linalian账户 - 开户绑卡信息保存
 */
export async function submitFinanceOpenparam(param: IFinanceLianAccOpening, personal = false) {
  param = JSON.parse(JSON.stringify(param)) as IFinanceLianAccOpening;

  let p = getFinanceLianAccOpeningParam(param as IFinanceLianAccOpening);

  if (personal) {
    p = {
      lianId: p.lianId,
      bankNo: p.bankNo,
      linkedPhone: p.linkedPhone,
    } as any;
  }

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/unified-lian-account/save-bank-account-info`,
      p,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 资金账户 - 申请linalian账户 - 受益人信息保存
 */
export async function submitFinanceBeneficiary(param: IBeneficiarySaveParam) {
  param = JSON.parse(JSON.stringify(param)) as IBeneficiarySaveParam;

  param.uboList.forEach((v) => {
    // @ts-ignore
    v.phoneNo = v.phone || undefined;
    v.idNoExpiryStartDate = changeTimeToFormat(v.idNoExpiryStartDate);
    v.idNoExpiryEndDate = changeTimeToFormat(v.idNoExpiryEndDate);

    delete v.idNoFrontType;
    delete v.idNoBackType;
  });

  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/unified-lian-account/save-beneficiary`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 资金账户 - 申请linalian账户 - 发送验证短信
 */
export async function sendFinanceLianSmscode(regPhone: string, lianId: number) {
  let token = '';

  try {
    const { code, context, message } = await Http.post<{ token: string }>(
      `${hostUrl}/pay/unified-lian-account/reg-phone-verify-code-apply`,
      {
        lianId,
        regPhone,
      },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }

    const d = context || {} as { token: string };

    token = d.token || '';
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return token;
}

/**
 * 资金账户 - 申请linalian账户 - 提交
 */
export async function submitFinanceLianLian(param: IFinanceLianApplySubmit) {
  let token = '';

  param = JSON.parse(JSON.stringify(param)) as IFinanceLianApplySubmit;

  try {
    const { code, context, message } = await Http.post<{ token: string }>(
      `${hostUrl}/pay/unified-lian-account/open-account-apply`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }

    token = (context || {}).token || '';
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return token;
}

/**
 * 资金账户 - 申请linalian账户 - 验证提交
 */
export async function submitFinanceLianVerify(param: IFinanceLianApplyVerify) {
  let token = '';

  param = JSON.parse(JSON.stringify(param)) as IFinanceLianApplyVerify;

  try {
    const { code, context, message } = await Http.post<{ token: string }>(
      `${hostUrl}/pay/unified-lian-account/open-account-verify`,
      param,
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      token = (context || {}).token || '';
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return token;
}

/**
 * 资金账户 - 连连提现
 */
export async function doLianLianWithdraw(params: IFinanceLianWithdraw) {
  let token = '';
  let txnId = 0;
  let revalid = false;

  try {
    const { code, context, message } = await Http.post<{ token: string; txnId: number }>(
      `${hostUrl}/pay/unified-lian/cash/withdrawal`,
      { ...params },
    );

    const d = context || {} as {token: string; txnId: number };

    token = d.token || '';
    txnId = d.txnId || 0;

    if (!isNull(token)) {
      revalid = true;

      return {
        token,
        txnId,
        revalid,
      };
    }

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    } else {
      return {
        token,
        txnId,
        revalid,
      };
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 连连提现 - 提现2次验证
 */
export async function doLianLianWithdrawVerify(params: IFinanceLianWithdrawVerify) {
  let token = '';

  try {
    const { code, context, message } = await Http.post<{ token: string }>(
      `${hostUrl}/pay/unified-lian/cash/validation-sms`,
      { ...params },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }

    token = (context || {}).token || '';
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return token;
}

/**
 * 获取资金账户 - lianlian - 账户列表
 */
export async function queryFinanceLianAccounts(param: IFinanceLianAccountListParam) {
  let total = 0;
  let list = [] as IFinanceLianAccount[];

  try {
    const { code, context, message } = await Http.post<{ data: IFinanceLianAccount[]; total: number }>(
      `${hostUrl}/pay/unified-lian-account/query/page`,
      { ...param },
    );

    if (code === EAxiosResponseCode.Succeed) {
      list = context?.data || [];
      total = context?.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    list,
    total,
  };
}

/**
 * 获取资金账户 - 连连账单 - 列表
 */
export async function queryFinanceLianBills(param: IFinanceLianBillListParam) {
  let total = 0;
  let list = [] as IFinanceLianBill[];

  try {
    const { code, context, message } = await Http.post<{ data: IFinanceLianBill[]; total: number }>(
      `${hostUrl}/pay/unified-lian-account/query/bills/page`,
      { ...param },
    );

    if (code === EAxiosResponseCode.Succeed) {
      list = context?.data || [];
      total = context?.total || 0;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    list,
    total,
  };
}

export async function queryLianBillsSum(params: IFinanceLianBillListParam) {
  let creditTotalAmt: number | undefined = 0;
  let debitTotalAmt: number | undefined = 0;

  try {
    const { code, context, message } = await Http.post<{ creditTotalAmt: number; debitTotalAmt: number }>(
      `${hostUrl}/pay/unified-lian-account/query/bills/summary`,
      { ...params },
    );

    if (code === EAxiosResponseCode.Succeed) {
      creditTotalAmt = context?.creditTotalAmt || undefined;
      debitTotalAmt = context?.debitTotalAmt || undefined;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return {
    creditTotalAmt,
    debitTotalAmt,
  };
}

/**
 * 资金账户 - 连连账单 - 导出
 */
export async function exportFinanceLianBills(params?: IFinanceLianBillListParam) {
  let buffer = null as Blob | null;

  try {
    const { code, context, message } = await Http.get<Blob>(
      `${hostUrl}/pay/unified-lian-account/query/bills/export`,
      { ...params },
      { responseType: 'blob' },
    );

    if (code === EAxiosResponseCode.Succeed) {
      buffer = context;
    } else {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }

  return buffer;
}

/**
 * 免密支付授权协议申请
 */
export async function queryFinanceLianNoPasswordAuthUrl(lianId: number) {
  try {
    const { code, context, message } = await Http.post<{ gatewayUrl: string }>(
      `${hostUrl}/pay/unified-lian-account/pap-agree-apply`,
      { lianId },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }

    return context?.gatewayUrl || '';
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 注销连连账户
 */
export async function cancelFinanceLianAccount(params: { randomKey: string; lianId: number; password: string }) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/unified-lian-account/manage/cancel-apply`,
      { ...params },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 资金账户 - 连连账户 - 修改手机号
 */
export async function changeFinanceLianPhone(params: IFinanceLianChangePhone) {
  try {
    const { code, context, message } = await Http.post<{ token: string; txnSeqno: string }>(
      `${hostUrl}/pay/unified-lian-account/manage/change-reg-phone-apply`,
      { ...params },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }

    return {
      token: context?.token || '',
      txnSeqno: context?.txnSeqno || '',
    };
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 资金账户 - 连连账户 - 修改手机号 - 验证
 */
export async function financeLianChangePhoneVerify(params: { token: string; lianId: number; verifyCode: string }) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/unified-lian-account/manage/change-reg-phone-verify`,
      { ...params },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 资金账户 - 连连账户 - 修改支付密码 - 发送验证码
 */
export async function financeLianUpdatePayPassBySms(lianId: number) {
  try {
    const { code, context, message } = await Http.post<{ token: string }>(
      `${hostUrl}/pay/unified-lian-account/manage/find-password-apply`,
      { lianId },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }

    return context?.token || '';
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 资金账户 - 连连账户 - 修改支付密码 - 验证
 */
export async function financeLianUpdatePayPassBySmsVerify(params: IFinanceLianUpdatePayPassBySms) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/unified-lian-account/manage/find-password-verify`,
      { ...params },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

/**
 * 资金账户 - 连连账户 - 修改支付密码 - 验证
 */
export async function financeLianUpdatePayPass(params: IFinanceLianUpdatePayPass) {
  try {
    const { code, message } = await Http.post<void>(
      `${hostUrl}/pay/unified-lian-account/manage/change-password`,
      { ...params },
    );

    if (code !== EAxiosResponseCode.Succeed) {
      throw new Error(message || '服务器异常，请稍后再试~');
    }
  } catch (error) {
    throw new Error(getHttpErrorMessage(error));
  }
}

